import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import './media-library.scss';

class MediaLibrary {
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-medialibrary',
            zipButtonAttr: 'data-medialibrary-zip',
            dataAttr: 'data-medialibrary-data',
            triggerValue: 'trigger'
        };

        this.settings = Object.assign({}, defaults, options);

        // Elements
        this.$root = element;
        this.$$trigger = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.triggerValue}"]`);
        this.$$zipButtons = this.$root.querySelectorAll(`[${this.settings.zipButtonAttr}]`);

        // Properties
        this.zips = null;
        this.folderNames = {};
    }

    initialize() {
        // Prepare all ZIP files asynchronously before setting events
        this.prepareZips();
        // Setup UI event handlers
        this.setEvents();
    }

    async prepareZips() {
        // Initialize object to store zip blobs keyed by button index
        this.zips = {};

        // Convert NodeList to Array to allow indexing
        const zipButtons = Array.from(this.$$zipButtons);

        // Loop through each zip button to generate corresponding ZIP
        for (let index = 0; index < zipButtons.length; index++) {
            const $zipButton = zipButtons[index];
            // Store folder name from button attribute
            this.folderNames[index] = $zipButton.getAttribute(this.settings.zipButtonAttr);
            const data = $zipButton.getAttribute(this.settings.dataAttr);

            if (!data) continue; // Skip if no data attribute present

            let parsedData;

            try {
                // Parse JSON data containing files info
                parsedData = JSON.parse(data);
            } catch (err) {
                console.warn(`Invalid JSON in button #${index}:`, err);
                continue;
            }

            const zip = new JSZip();
            // Create a folder inside the zip with the folder name
            const folder = zip.folder(this.folderNames[index]);

            // Fetch all files concurrently and add them to the folder
            await Promise.all(parsedData.map(async (file) => {
                try {
                    const response = await fetch(file.url);
                    if (!response.ok) throw new Error(`Failed to load: ${file.url}`);
                    const blob = await response.blob();
                    folder.file(file.name, blob);
                } catch (err) {
                    console.error(`Error loading file #${index}:`, err);
                }
            }));

            // Generate the zip file as a Blob
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Calculate size in MB with 2 decimals
            const mb = zipBlob.size / (1024 * 1024);
            const visibleSize = `${mb.toFixed(2)} MB`;

            // Create a text node with the size string
            const newTextNode = document.createTextNode(' ' + visibleSize + ' ');

            // Insert the size text before the first child element of the button
            $zipButton.insertBefore(newTextNode, $zipButton.firstElementChild);
            // Make sure button is visible
            $zipButton.removeAttribute('hidden');

            // Store the generated zip blob under the current index
            this.zips[index] = zipBlob;
        }
    }

    triggerDownload(index) {
        // Retrieve the zip blob for the given index
        const zipBlob = this.zips?.[index];

        if (zipBlob) {
            // Use folder name as the filename for the zip
            const filename = `${this.folderNames[index]}.zip`;
            // Trigger the download using FileSaver.js
            saveAs(zipBlob, filename);
        } else {
            alert('ZIP has not been prepared or is empty.');
        }
    }

    setEvents() {
        // Set toggle events for the triggers
        this.$$trigger.forEach($trigger => {
            $trigger.addEventListener('click', () => {
                const isOpen = $trigger.getAttribute('aria-expanded') === 'true';
                const relatedContentId = $trigger.getAttribute('aria-controls');
                const $relatedContent = this.$root.querySelector(`#${CSS.escape(relatedContentId)}`);

                if ($relatedContent) {
                    // Toggle aria attributes for accessibility
                    $relatedContent.setAttribute('aria-hidden', isOpen);
                    $trigger.setAttribute('aria-expanded', !isOpen);
                }
            });
        });

        // Set click events on zip buttons to trigger downloads
        this.$$zipButtons.forEach(($zipButton, index) => {
            $zipButton.addEventListener('click', () => {
                this.triggerDownload(index);
            });
        });
    }
}

export { MediaLibrary };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-medialibrary="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new MediaLibrary($root);
            $rootAPI.initialize();
        });
    }
});
